import { useNavigate } from '@tanstack/react-router';
import { AuthLayout } from '@/features/auth';
import { SetupForm, useSetupStatus, useCompleteSetup } from '@/features/setup';
import { useErrorHandler } from '@/hooks';
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

export function SetupPage() {
  const navigate = useNavigate();
  const { data: setupStatus, isLoading: isStatusLoading, error: statusError } = useSetupStatus();
  const { mutate: completeSetup, isPending, error } = useCompleteSetup();
  const { handleError, handleSuccess } = useErrorHandler('Setup');
  // Interactive override: cleared on submit, set by the onError callback.
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Derive the displayed error during render rather than mirroring the mutation
  // error into state via an effect. The override takes precedence so a fresh
  // submit can clear a stale message.
  const setupError =
    submitError ?? (error instanceof Error ? error.message : error ? 'An error occurred during setup' : null);

  // Redirect if setup is not required (already completed)
  useEffect(() => {
    if (setupStatus && !setupStatus.setup_required) {
      navigate({ to: '/' });
    }
  }, [setupStatus, navigate]);

  // Surface the genuine side-effect (toast) when a mutation error appears.
  useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error, handleError]);

  const handleSetup = async (data: Parameters<typeof completeSetup>[0]) => {
    setSubmitError(null);
    completeSetup(data, {
      onSuccess: () => {
        handleSuccess('Setup completed! You are now logged in.');
        navigate({ to: '/' });
      },
      onError: (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Setup failed. Please try again.';
        setSubmitError(errorMessage);
      },
    });
  };

  // Show loading state while fetching setup status
  if (isStatusLoading) {
    return (
      <AuthLayout title="Welcome to Shipcast" subtitle="Checking setup status...">
        <div className="flex justify-center py-8">
          <Loader2 className="size-8 animate-spin text-action-primary" />
        </div>
      </AuthLayout>
    );
  }

  // Show error state if we can't fetch setup status
  if (statusError) {
    return (
      <AuthLayout title="Connection Error" subtitle="Unable to connect to the server">
        <div className="py-4 text-center">
          <p className="mb-4 text-feedback-error">
            Could not reach the API server. Please ensure the server is running.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="
              text-action-primary
              hover:underline
            "
          >
            Try again
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Welcome to Shipcast"
      subtitle="Complete the initial setup to create your admin account"
      footer={<>This is a one-time setup that creates the first administrator account.</>}
    >
      <SetupForm onSubmit={handleSetup} isLoading={isPending} error={setupError} />
    </AuthLayout>
  );
}
